import useDimensionsAccessLevel from '~/app/shared/dimensions/hooks/useDimensionsAccessLevel';
import { DimensionsAccess } from '~/app/shared/dimensions/types';
import useQuery from '~/app/shared/hooks/useQuery';
import {
    DimensionData,
    DimensionLine,
    DimensionLineTypeEnum,
    DistributionTemplateDistribution,
    MultipleDimension,
} from '~/types/internal-api';

/**
 * Hard-coded ID's of dimension that are mapped to the database.
 */
export type MultipleDimensionId = 1 | 2 | 3;

interface DimensionFormData {
    dimension1?: number;
    dimension2?: number;
    dimension3?: number;
}

/**
 * Contains integration of multi-dimension to the new journal.
 */
export default function useJournalEntryDimensions(): {
    loading: boolean;
    dimensions: MultipleDimension[];
    getById: (id: MultipleDimensionId) => MultipleDimension | undefined;
    getNameByTextId: (textId: string) => string | null;
    convertToPostData: (data: DimensionFormData) => DimensionData[];
    convertToFormData: (data: DimensionData[]) => DimensionFormData | undefined;
    mapDistributions: (formDistributionsValues: DistributionTemplateDistribution[]) => DistributionTemplateDistribution[];
} {
    const { loading, data: dimensions } = useQuery<MultipleDimension[]>('dimension/GetAll', []);
    const hasMultiDimensions = useDimensionsAccessLevel(DimensionsAccess.HasMultiDimensions);

    function getNameByTextId(textId: string): string | null {
        let dimensionId: MultipleDimensionId | null;
        switch (textId?.toUpperCase()) {
            case 'LEDGER_TABORDER_MULTI_DIMENSIONS_1':
                dimensionId = 1;
                break;
            case 'LEDGER_TABORDER_MULTI_DIMENSIONS_2':
                dimensionId = 2;
                break;
            case 'LEDGER_TABORDER_MULTI_DIMENSIONS_3':
                dimensionId = 3;
                break;
            default:
                dimensionId = null;
        }

        return dimensions.find(d => d.id === dimensionId)?.name ?? null;
    }

    function getById(id: MultipleDimensionId): MultipleDimension | undefined {
        return dimensions.find(d => d.id === id);
    }

    function getByIdAndKey(id: MultipleDimensionId, key: number): DimensionLine | undefined {
        return dimensions.find(d => d.id === id)?.lines?.find(l => l.key === key);
    }

    function convertToFormData(data: DimensionData[]): DimensionFormData | undefined {
        let formData: DimensionFormData = {};
        data.forEach(dimension => {
            if (dimension.dimensionId === 1) {
                formData = { ...formData, dimension1: dimension.dimensionKey };
            }
            if (dimension.dimensionId === 2) {
                formData = { ...formData, dimension2: dimension.dimensionKey };
            }
            if (dimension.dimensionId === 3) {
                formData = { ...formData, dimension3: dimension.dimensionKey };
            }
        });

        return formData;
    }
    function convertToPostData(data: DimensionFormData) {
        const postData: DimensionData[] = [];

        if (data.dimension1) {
            const line = getByIdAndKey(1, data.dimension1);
            if (line) {
                postData.push({
                    dimensionId: 1,
                    dimensionKey: line?.key,
                    isDistribution: line.type === DimensionLineTypeEnum.DimensionDistribution,
                });
            }
        }

        if (data.dimension2) {
            const line = getByIdAndKey(2, data.dimension2);
            if (line) {
                postData.push({
                    dimensionId: 2,
                    dimensionKey: line?.key,
                    isDistribution: line.type === DimensionLineTypeEnum.DimensionDistribution,
                });
            }
        }

        if (data.dimension3) {
            const line = getByIdAndKey(3, data.dimension3);
            if (line) {
                postData.push({
                    dimensionId: 3,
                    dimensionKey: line?.key,
                    isDistribution: line.type === DimensionLineTypeEnum.DimensionDistribution,
                });
            }
        }

        return postData;
    }
    function mapDistributions(formDistributionsValues: DistributionTemplateDistribution[]) {
        return formDistributionsValues.reduce((acc, value) => {
            const updatedValue = { ...value };

            if (value.departmentNumber && hasMultiDimensions) {
                updatedValue.dimensionData = [
                    {
                        dimensionId: 1,
                        dimensionKey: value.departmentNumber,
                    },
                ];
            }

            return [...acc, updatedValue];
        }, [] as DistributionTemplateDistribution[]);
    }

    return { loading, dimensions, getById, getNameByTextId, convertToPostData, convertToFormData, mapDistributions };
}
